<template>
  <div
    v-if="!loading"
    class="beauty-regimen"
  >
    <h5 class="beauty-regimen-title">
      {{ title }}
    </h5>

    <div class="beauty-regimen-container">
      <div class="beauty-regimen-body">
        <div
          v-for="item, index in items"
          :key="item.identifier.productId"
          class="grid-padding-y"
        >
          <div
            v-if="index !== 0"
            class="grid-x beauty-regimen-item-delimiter"
          >
            <div class="small-4">
              <hr>
            </div>
            <div class="small-4 grid-x beauty-regimen-item-category">
              <div class="small-4">
                <span class="accordion-plus" />
              </div>
              <div class="small-8">
                <h6 v-if="item.productTypeOrder === 1">
                  Cleanse
                </h6>
                <h6 v-if="item.productTypeOrder === 2">
                  Treat
                </h6>
                <h6 v-if="item.productTypeOrder === 3">
                  Target
                </h6>
                <h6 v-if="item.productTypeOrder === 4">
                  Hydrate
                </h6>
              </div>
            </div>
            <div class="small-4">
              <hr>
            </div>
          </div>
          <div class="beauty-regimen-item grid-x cell">
            <input
              type="checkbox"
              :checked="checkboxValues.includes(item.identifier.productId)"
              @click="onClickCheckbox(item.identifier.productId)"
            >
            <div class="small-3 beauty-regimen-item-image">
              <a
                :href="item.identifier.productUrl"
                tabindex="0"
                :target="target"
                :rel="rel"
                @click="onClickProductThumbnail(item.identifier.productId, index)"
              >
                <img
                  :id="'img_' + item.identifier.productId"
                  class="product-thumbnail-image"
                  alt=""
                  loading="lazy"
                  :src="getImage(item)"
                >
              </a>
            </div>
            <div class="small-x beauty-regimen-item-description">
              <template v-if="index === 0">
                <h6 class="beauty-regimen-anchor-title">
                  This item
                </h6>
                <a
                  :href="item.identifier.productUrl"
                  class="beauty-regimen-item-title"
                  :title="item.detail.title ? item.detail.title : item.detail.name"
                  :target="target"
                  :rel="rel"
                  @click="onClickProductThumbnail(item.identifier.productId, index)"
                >
                  {{ item.detail.displayName }}
                </a>
              </template>
              <template v-else>
                <div class="grid-x">
                  <div :class="item.isMultiUpc ? 'small-10' : 'small-x'">
                    <a
                      :href="item.identifier.productUrl"
                      :title="item.detail.title ? item.detail.title : item.detail.name"
                      class="beauty-regimen-item-title"
                      :target="target"
                      :rel="rel"
                      @click="onClickProductThumbnail(item.identifier.productId, index)"
                    >
                      {{ item.detail.displayName }}
                    </a>
                  </div>
                  <div
                    v-if="item.isMultiUpc"
                    role="button"
                    class="small-2 beauty-regimen-item-edit"
                    @click.stop="handleEdit(item.identifier.productId)"
                  >
                    Edit
                  </div>
                </div>
              </template>
              <div
                v-if="item.pricing.price.labelWithPrice"
                class="beauty-regimen-item-price"
              >
                {{ item.pricing.price.labelWithPrice }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="beauty-regimen-footer">
        <button
          id="beauty-regimen-add-to-bag"
          class="button primary"
          :disabled="buttonDisabled"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
  <EditItemDrawer
    id="pros-edit-item-drawer"
    :product="editedItem"
    :open-in-new-tab="openInNewTab"
    @updateItem="handleUpdateItem"
    @closeSlideout="handleCloseDrawer"
  />
</template>

<script>
import { shallowRef } from 'vue';
import { mapMutations } from 'vuex';
import { fireTag } from '@vue-util/tagManager';
import fetchProducts from '../../services/fetchProducts';
import { isValidProduct } from '../../utils/utils';
import EditItemDrawer from './EditItemDrawer';
import { fireImpressionLinkTagging } from '../../analytics/analyticsEvent';

const SKIN_CARE_TYPES = [
  'cleansers',
  'serums',
  'eye treatments',
  'moisturizers',
];

export default {
  name: 'ProsBeautyRegimen',
  components: { EditItemDrawer },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
    shouldSelectProductsOnStart: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['dataLoadStatusEvent'],
  data() {
    // mostly just rendering items with deep nesting
    // and not tracking prop changes
    const shallowItems = shallowRef([]);
    return {
      items: shallowItems,
      checkboxValues: [],
      buttonText: '',
      loading: true,
      editedItemId: null,
    };
  },
  computed: {
    title() {
      return this.configData.title || 'Complete your routine';
    },
    buttonDisabled() {
      return this.checkboxValues.length === 0;
    },
    editedItem() {
      return this.items.find((item) => item.identifier.productId === this.editedItemId) || null;
    },
    openInNewTab() {
      return this.configData.openInNewTab;
    },
    target() {
      return this.openInNewTab ? '_blank' : '_self';
    },
    rel() {
      return this.openInNewTab ? 'noopener' : null;
    },
  },
  watch: {
    checkboxValues: {
      handler() {
        this.updateButtonText();
      },
      deep: true,
    },
    items(newVal) {
      if (newVal.length > 0) {
        fireImpressionLinkTagging({
          products: newVal,
          recommendedItems: this.configData?.recommendationData?.recommendedIdsList,
          anchorProductId: this.configData?.recommendationConfig?.productId,
          zone: this.configData?.recommendationConfig?.zone,
          type: 'presented',
        });
      }
    },
  },
  async mounted() {
    if (
      !this.configData.recommendationConfig?.productId
        || !this.configData.recommendationData?.recommendedIdsList?.length > 0
    ) return;
    try {
      await this.getProductsToRender();
      if (this.shouldSelectProductsOnStart) {
        this.selectAllProducts();
      }
    } catch (e) {
      // error goes here
    }
  },
  methods: {
    ...mapMutations({
      toggle: 'slideoutStore/TOGGLE',
    }),
    getImage(item) {
      return `${item.urlTemplate.product}${item.imagery.images[0].filePath}`
        + '?op_sharpen=1&wid=240&hei=300&fit=fit,1&qlt=50&layer=comp&op_sharpen=0&resMode=bicub&op_usm=0.7,1.0,0.5,0&fmt=jpeg';
    },
    updateButtonText() {
      const checkedItemsLength = this.checkboxValues.length;
      if (checkedItemsLength > 0) {
        const selectedItems = [];
        this.checkboxValues.forEach((id) => {
          const selectedItem = this.items.find((item) => item.identifier.productId === id);
          selectedItems.push(selectedItem);
        });
        const totalPrice = selectedItems.reduce((acc, value) => acc + value.pricing.price.tieredPrice[0].values[0].value, 0);
        this.buttonText = `Add To Bag (${checkedItemsLength} ${checkedItemsLength > 1 ? 'items' : 'item'}) $${totalPrice.toFixed(2)}`;
      } else {
        this.buttonText = 'Make A Selection';
      }
    },
    filterRecommendedProducts(productsList) {
      const matchFilterCondition = (product) => (Object.keys(product.relationships?.upcs || {}).length > 1
        ? (!product.traits?.sizes || Number.isInteger(product.traits?.sizes.selectedSize))
        : true)
                && !!product.detail.skinCareType
                && SKIN_CARE_TYPES.includes(product.detail.skinCareType.toLowerCase());
      const filteredProducts = productsList.filter(matchFilterCondition);
      if (filteredProducts.length === 0) {
        return [];
      }
      const result = [];
      const { recommendedIdsList: recommendedItems } = this.configData?.recommendationData;
      filteredProducts.forEach((product) => {
        const { score, productType } = recommendedItems.find((item) => item.id === product.id) || { score: 0, productType: 'beauty' };
        product.score = score;
        product.productTypeOrder = SKIN_CARE_TYPES.indexOf(productType.toLowerCase()) + 1;
      });
      const skinCareTypeGroups = filteredProducts.reduce((acc, item) => {
        if (!Object.prototype.hasOwnProperty.call(acc, item.productTypeOrder)) {
          acc[item.productTypeOrder] = [];
        }
        return { ...acc, ...acc[item.productTypeOrder].push(item) };
      }, {});

      Object
        .values(skinCareTypeGroups)
        .forEach((group) => {
          result.push(group.sort((a, b) => parseFloat(b.score) - parseFloat(a.score))[0]);
        });
      return result;
    },
    async getProductsToRender() {
      const mainItem = fetchProducts([this.configData?.recommendationConfig?.productId], 1, isValidProduct, false, { size: 'large' });
      const additionalItems = fetchProducts(this.configData?.recommendationData?.recommendedIdsList
        ?.map((item) => item.id)
        .slice(1), this.configData?.recommendationData?.recommendedIdsList.length - 1, isValidProduct, false, { size: 'large' });
      const items = (await Promise.allSettled([mainItem, additionalItems])).map((p) => p.value).flat();
      const anchorProduct = items[0];
      const productsList = items.slice(1);
      const filteredItems = this.filterRecommendedProducts(productsList);

      this.items = [anchorProduct, ...filteredItems];
      this.items.forEach((product) => {
        product.detail.displayName = product.detail.displayName ? product.detail.displayName : product.detail.completeName;
        product.pricing.price.labelWithPrice = product.pricing.price.labelWithPrice
          ? product.pricing.price.labelWithPrice
          : product.pricing.price.tieredPrice[0]?.values[0]?.formattedValue;
        if (product.relationships?.memberProductIds && product.relationships.memberProductIds.length > 1) {
          product.isMultiUpc = true;
          const { identifier: { productId: upcWithLowestPriceId } } = Object.values(product.relationships.memberProductMap).find(
            (upc) => upc.pricing.price.tieredPrice[0]?.values[0]?.formattedValue === product.pricing.price.labelWithPrice,
          );
          const chippestProduct = product.relationships.memberProductMap[upcWithLowestPriceId];
          product.detail.displayName = chippestProduct.detail.brand.name ? `${chippestProduct.detail.brand.name} ${chippestProduct.detail.pdfEmailDescription}` : `${chippestProduct.detail.brand} ${chippestProduct.detail.pdfEmailDescription}`;
          // patch to get consistency with Thumbnail model
          if (product.traits?.colors?.colorMap && !Array.isArray(product.traits?.colors?.colorMap)) {
            product.traits.colors.colorMap = Object.values(product.traits.colors.colorMap);
          }
          if (product?.traits?.sizes?.sizeMap && Object.keys(product?.traits?.sizes?.sizeMap).length > 0) {
            // pricing policy popup link's data
            Object.keys(product.traits.sizes.sizeMap).forEach((size) => {
              product.traits.sizes.sizeMap[size].pricing.price.policy = product.traits.sizes.sizeMap[size].pricing.price.policy
                ? product.traits.sizes.sizeMap[size].pricing.price.policy
                : {
                  text: '',
                  url: '',
                };
            });
            // all provided sizes added as available
            if (product?.traits?.colors?.selectedColor && !product.traits.colors.selectedColor.sizes) {
              product.traits.colors.selectedColor = {
                id: product.traits.colors.selectedColor,
                sizes: product.traits.colors.colorMap.reduce((sizes, color) => [...sizes, ...color.sizes], []),
              };
            }
          }
        }
      });
      this.updateButtonText();
      this.loading = false;
    },
    onClickCheckbox(option) {
      if (this.checkboxValues.includes(option)) {
        this.checkboxValues.splice(this.checkboxValues.indexOf(option), 1);
      } else {
        this.checkboxValues.push(option);
      }
    },
    handleEdit(id) {
      this.editedItemId = id;
      this.toggle('pros-edit-item-drawer');
    },
    handleUpdateItem(editedProductId, memberProductIndex) {
      this.toggle('pros-edit-item-drawer');

      const productToEdit = this.items.find(({ id }) => id === editedProductId);
      const upcToUse = productToEdit.relationships.memberProductIds[memberProductIndex];
      const selectedProduct = productToEdit.relationships.memberProductMap[upcToUse];

      // assign values from master product to updated sized product
      selectedProduct.id = parseInt(upcToUse, 10);

      selectedProduct.pricing.price.labelWithPrice = selectedProduct.pricing.price.tieredPrice[0].values[0].formattedValue;
      selectedProduct.imagery = Object.values(selectedProduct.traits.colors?.colorMap || {})[0].imagery || productToEdit.imagery;
      selectedProduct.urlTemplate = productToEdit.urlTemplate;
      selectedProduct.detail.displayName = selectedProduct.detail.brand.name ? `${selectedProduct.detail.brand.name} ${selectedProduct.detail.pdfEmailDescription}` : `${selectedProduct.detail.brand} ${selectedProduct.detail.pdfEmailDescription}`;

      selectedProduct.traits = productToEdit.traits;
      selectedProduct.traits.sizes.selectedSize = memberProductIndex;
      selectedProduct.relationships = productToEdit.relationships;
      selectedProduct.detail.brand = productToEdit.detail.brand;
      selectedProduct.productTypeOrder = productToEdit.productTypeOrder;
      selectedProduct.detail.completeName = productToEdit.detail.completeName;

      if (productToEdit.relationships.memberProductIds.length > 1) {
        selectedProduct.isMultiUpc = true;
      }
      // remove initial selectedProduct from checked items and update with updated selected product
      if (this.checkboxValues.includes(productToEdit.id)) {
        const index = this.checkboxValues.findIndex((id) => id === productToEdit.id);
        this.checkboxValues.splice(index, 1, selectedProduct.id);
      }

      const productToReplaceIndex = this.items.findIndex((product) => product.id === productToEdit.id);
      // saving a new array of items to fire re-render as we use shallowRef to store items
      this.items = [...this.items.slice(0, productToReplaceIndex), selectedProduct, ...this.items.slice(productToReplaceIndex + 1)];
    },
    onClickProductThumbnail(productId, position) {
      const { recommendedIdsList: recommendedItems = [] } = this.configData?.recommendationData || {};
      const { productId: anchorProductId, zone } = this.configData?.recommendationConfig || {};
      const selectedItem = this.items.find((item) => item.identifier.productId === productId);

      fireImpressionLinkTagging({
        products: [selectedItem], recommendedItems, anchorProductId, zone,
      });

      if (anchorProductId && (anchorProductId !== productId)) {
        try {
          const { choiceId = '' } = recommendedItems.shift() || {};
          const { treatmentId, headerText } = this.configData?.recommendationData || {};
          const model = choiceId.substring(6, 9);
          fireTag('link', {
            event_name: 'recommendation link click',
            recommendation_model: model,
            recommendation_title: headerText,
            recommendation_page_type: 'product',
            recommendation_zone_pos: position !== undefined ? position.toString() : '',
            recommendation_zone: zone,
            recommendation_milanid: (treatmentId && treatmentId.toString() !== 'blank') ? treatmentId : '',
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`recommendationLinkTag fail ${e}`);
        }
      }
    },
    handleCloseDrawer() {
      this.editedItemId = null;
    },
    selectAllProducts() {
      this.checkboxValues = this.items.map((item) => item.identifier.productId);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@core/csg/src/scss/encodedIcons/bcom/_accordion-plus';
@import "~@core/csg/src/scss/forms/forms";

.beauty-regimen {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  .beauty-regimen-title {
    font-size: 16px;
    line-height: 20px; /* 125% */
  }

  .beauty-regimen-container {
    @include csg-forms;
    border: 1px solid $gray-3-color;
    width: 100%;

    input[type="checkbox"]:focus {
      outline: 0;
    }

    input[type="checkbox"] {
      margin-right: 0;

      &:focus {
        outline: 0;
      }
    }
  }

  .beauty-regimen-body {
    padding: 1.5rem 1rem;
  }

  .beauty-regimen-footer {
    background-color: $background-color;
    padding: 16px;

    button {
      width: 100%;
    }
  }

  .beauty-regimen-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;

    &.cell {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }

    align-items: start;
  }

  .beauty-regimen-anchor-title {
    margin-bottom: 4px;
  }

  .beauty-regimen-item-image {
    width: 68px !important;
    height: 85px !important;
  }

  .beauty-regimen-item-description {
    flex: 1;
    align-self: flex-start;
  }

  .beauty-regimen-item-title {
    text-decoration: none;
  }

  .beauty-regimen-item-price {
    margin-top: 8px;
    font-weight: 500;
  }

  .beauty-regimen-container-edit {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
  }

  .beauty-regimen-item-edit {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.01em;
    color: $black;
    text-decoration: underline;
    cursor: pointer;
    text-align: right;
  }

  .beauty-regimen-item-delimiter {
    align-items: center;
    height: 24px;

    hr {
      border: 0.5px solid $gray-3-color;
    }

  }

  .beauty-regimen-item-category {
    align-items: center;
    display: flex;
    padding-top: 1px;

    div {
      display: flex;
      justify-content: center;

      &:first-child {
        justify-content: end;
      }
    }
  }
}
</style>
